import type { AnnualCrossTopicCountScope } from './annualProgressCrossTopicExtract';
import type { AnnualCrossTopicDetailRow } from './annualProgressCrossTopicExtract';
import type {
  AnnualOperationPlanExportClassHeader,
  AnnualOperationPlanExportRow,
  AnnualOperationPlanExportWeekday,
} from './annualOperationPlanExcelExport';
import type {
  NeisLearningContentUploadRow,
  NeisLearningContentUploadViewMode,
  NeisUploadLearningContentRow,
} from './neisLearningContentUploadRows';
import { openBlankTabWithAppTitle, openSameOriginWindow } from './appDocumentTitle';

const STORAGE_PREFIX = 'cp.documentExportPreview.v1:';
const STASH_LS_MAX = 8;

export type DocumentExportPreviewAction = 'view' | 'pdf' | 'print';

export type AnnualOperationPlanDocumentClassHeader = AnnualOperationPlanExportClassHeader & {
  gradeId: string;
};

export type AnnualOperationPlanDocumentPayload = {
  title: string;
  metaSubtitle: string;
  scopeClassTitle?: string;
  scopeGradeNumbers?: string[];
  weekdays: AnnualOperationPlanExportWeekday[];
  classHeaders: AnnualOperationPlanDocumentClassHeader[];
  rows: AnnualOperationPlanExportRow[];
  totalInstructionalDays: number;
  totalHoursByColumnKey: Record<string, number | string>;
  vacationText: string;
  footerRows?: Array<{
    kind: 'subtotal' | 'grandTotal';
    label: string;
    termNumber?: 1 | 2;
    totalInstructionalDays: number;
    totalHoursByColumnKey: Record<string, number | string>;
    vacationText?: string;
  }>;
  tableBodyItems?: Array<
    | { kind: 'week'; row: AnnualOperationPlanExportRow }
    | {
        kind: 'footer';
        footer: {
          kind: 'subtotal' | 'grandTotal';
          label: string;
          termNumber?: 1 | 2;
          totalInstructionalDays: number;
          totalHoursByColumnKey: Record<string, number | string>;
          vacationText?: string;
        };
      }
  >;
};

export type AnnualCrossTopicDetailDocumentPayload = {
  topic: string;
  topicTrackLabel?: string;
  topicTimetableLabel?: string;
  scope: AnnualCrossTopicCountScope;
  scopeLabel: string;
  rows: AnnualCrossTopicDetailRow[];
};

export type AnnualCrossTopicProgramPlanDocumentPayload = {
  scope: AnnualCrossTopicCountScope;
  scopeLabel: string;
  documentTitle: string;
  groups: Array<{
    topic: string;
    rows: AnnualCrossTopicDetailRow[];
  }>;
};

export type NeisLearningContentUploadDocumentPayload = {
  termNumber: 1 | 2;
  headerTitle: string;
  showDateColumns: boolean;
  viewMode: NeisLearningContentUploadViewMode;
  rows: Array<NeisLearningContentUploadRow | NeisUploadLearningContentRow>;
};

export type DocumentExportPreviewPayloadV1 =
  | {
      v: 1;
      kind: 'annual-operation-plan';
      fileNameBase: string;
      printTitle: string;
      data: AnnualOperationPlanDocumentPayload;
    }
  | {
      v: 1;
      kind: 'annual-cross-topic-detail';
      fileNameBase: string;
      printTitle: string;
      data: AnnualCrossTopicDetailDocumentPayload;
    }
  | {
      v: 1;
      kind: 'annual-cross-topic-program-plan';
      fileNameBase: string;
      printTitle: string;
      data: AnnualCrossTopicProgramPlanDocumentPayload;
    }
  | {
      v: 1;
      kind: 'program-learning-content-detail';
      fileNameBase: string;
      printTitle: string;
      data: AnnualCrossTopicDetailDocumentPayload;
    }
  | {
      v: 1;
      kind: 'neis-learning-content-upload';
      fileNameBase: string;
      printTitle: string;
      data: NeisLearningContentUploadDocumentPayload;
    };

export type OpenDocumentExportPreviewFailureReason = 'stash_failed' | 'popup_blocked';

export type OpenDocumentExportPreviewResult =
  | { ok: true }
  | { ok: false; reason: OpenDocumentExportPreviewFailureReason };

function stashKey(id: string): string {
  return STORAGE_PREFIX + id;
}

function newStashId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;
}

function localStorageSafe(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function sessionStorageSafe(tab?: Window | null): Storage | null {
  try {
    if (tab && !tab.closed) {
      return tab.sessionStorage;
    }
  } catch {
    /* ignore */
  }
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function pruneOldStashEntries(): void {
  const ls = localStorageSafe();
  if (!ls) return;
  const keys: string[] = [];
  for (let i = 0; i < ls.length; i += 1) {
    const key = ls.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  keys.sort();
  while (keys.length > STASH_LS_MAX) {
    const oldest = keys.shift();
    if (oldest) ls.removeItem(oldest);
  }
}

function writeStash(key: string, serialized: string, targetTab?: Window | null): boolean {
  const session = sessionStorageSafe(targetTab);
  if (session) {
    try {
      session.setItem(key, serialized);
      return true;
    } catch {
      /* fall through */
    }
  }
  const ls = localStorageSafe();
  if (!ls) return false;
  try {
    pruneOldStashEntries();
    ls.setItem(key, serialized);
    return true;
  } catch {
    return false;
  }
}

function readStash(key: string): string | null {
  try {
    const fromSession = window.sessionStorage.getItem(key);
    if (fromSession) return fromSession;
  } catch {
    /* ignore */
  }
  const ls = localStorageSafe();
  if (!ls) return null;
  try {
    return ls.getItem(key);
  } catch {
    return null;
  }
}

function parsePayload(raw: string): DocumentExportPreviewPayloadV1 | null {
  try {
    const parsed = JSON.parse(raw) as DocumentExportPreviewPayloadV1;
    if (!parsed || parsed.v !== 1 || !parsed.kind || !parsed.data) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function stashDocumentExportPreviewPayload(
  payload: DocumentExportPreviewPayloadV1,
  targetTab?: Window | null
): string {
  const id = newStashId();
  const key = stashKey(id);
  const serialized = JSON.stringify(payload);
  if (writeStash(key, serialized, targetTab)) return id;
  return '';
}

export function readDocumentExportPreviewPayload(id: string): DocumentExportPreviewPayloadV1 | null {
  const trimmed = id.trim();
  if (!trimmed) return null;
  const raw = readStash(stashKey(trimmed));
  if (!raw) return null;
  return parsePayload(raw);
}

export function buildDocumentExportPreviewUrl(
  id: string,
  action: DocumentExportPreviewAction = 'view'
): string {
  const basePath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const params = new URLSearchParams({ k: id, action });
  const path = `${basePath}/document-export-preview?${params.toString()}`;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

export function openDocumentExportPreviewTabPlaceholder(): Window | null {
  return openBlankTabWithAppTitle();
}

export function closeDocumentExportPreviewTabPlaceholder(tab: Window | null | undefined): void {
  try {
    tab?.close();
  } catch {
    /* ignore */
  }
}

export function openDocumentExportPreviewFailureMessage(
  reason: OpenDocumentExportPreviewFailureReason
): string {
  if (reason === 'stash_failed') {
    return '문서 미리보기 탭을 열 수 없습니다. 브라우저 저장 공간을 비운 뒤 다시 시도해 주세요.';
  }
  return '문서 미리보기 탭이 차단되었습니다. 브라우저 팝업 허용 후 다시 시도해 주세요.';
}

export function openDocumentExportPreviewInNewTab(
  payload: DocumentExportPreviewPayloadV1,
  action: DocumentExportPreviewAction,
  options?: { preOpenedWindow?: Window | null }
): OpenDocumentExportPreviewResult {
  const preOpened = options?.preOpenedWindow ?? null;
  const id = stashDocumentExportPreviewPayload(payload, preOpened);
  if (!id.trim()) {
    closeDocumentExportPreviewTabPlaceholder(preOpened);
    return { ok: false, reason: 'stash_failed' };
  }
  const url = buildDocumentExportPreviewUrl(id, action);
  if (preOpened) {
    try {
      if (preOpened.closed) {
        closeDocumentExportPreviewTabPlaceholder(preOpened);
        return { ok: false, reason: 'popup_blocked' };
      }
      preOpened.location.replace(url);
      preOpened.focus?.();
      return { ok: true };
    } catch {
      closeDocumentExportPreviewTabPlaceholder(preOpened);
      const fallback = openSameOriginWindow(url);
      if (!fallback) return { ok: false, reason: 'popup_blocked' };
      return { ok: true };
    }
  }
  const w = openSameOriginWindow(url);
  if (!w) return { ok: false, reason: 'popup_blocked' };
  return { ok: true };
}
